import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/apiConfig';
import buildImageUrl from '../config/imageUrlHelper';

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const FLATICON_ICON_PROFILE = 'https://cdn-icons-png.flaticon.com/512/5588/5588116.png';
const FLATICON_ICON_UPLOAD = 'https://cdn-icons-png.flaticon.com/512/17240/17240671.png';
const FLATICON_ICON_DELETE = 'https://cdn-icons-png.flaticon.com/512/4502/4502939.png';
const FLATICON_ICON_URL = 'https://cdn-icons-png.flaticon.com/512/376/376218.png';

const getIconClass = (size = 'w-4 h-4') => `${size} object-contain dark:invert`;

const ProfileImageUploadField = ({ value, onUploaded, disabled = false }) => {
  const inputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadedPath, setUploadedPath] = useState('');
  const [previewBuster, setPreviewBuster] = useState(0);
  const [urlValue, setUrlValue] = useState('');
  const [urlPreviewFailed, setUrlPreviewFailed] = useState(false);

  const appendCacheBuster = (url, buster) => {
    if (!url) return '';
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${buster}`;
  };

  const previewSrc = useMemo(() => {
    const selectedPath = uploadedPath || value || '';
    if (!selectedPath) return '';

    const resolvedUrl = buildImageUrl(selectedPath);
    return appendCacheBuster(resolvedUrl, previewBuster);
  }, [uploadedPath, value, previewBuster]);

  const validateUrl = (urlText) => {
    if (!urlText) return 'Debes pegar una URL de imagen.';

    let parsedUrl;
    try {
      parsedUrl = new URL(urlText);
    } catch (parseError) {
      return 'La URL no es valida.';
    }

    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return 'La URL debe comenzar por http:// o https://';
    }

    return '';
  };

  const normalizedUrlInput = String(urlValue || '').trim();

  const urlPreviewSrc = useMemo(() => {
    if (!normalizedUrlInput) return '';
    if (validateUrl(normalizedUrlInput)) return '';
    return buildImageUrl(normalizedUrlInput);
  }, [normalizedUrlInput]);

  useEffect(() => {
    setUrlPreviewFailed(false);
  }, [normalizedUrlInput]);

  const validateFile = (file) => {
    if (!file) return 'No se selecciono ningun archivo.';
    if (!file.type || !file.type.startsWith('image/')) {
      return 'Debes seleccionar una imagen valida.';
    }
    if (file.size <= 0 || file.size > MAX_IMAGE_SIZE_BYTES) {
      return 'La imagen debe pesar entre 1 byte y 5 MB.';
    }
    return '';
  };

  const handleUpload = async (file) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setIsUploading(true);

    try {
      const payload = new FormData();
      payload.append('profile_image', file);

      const response = await axios.post(`${API_BASE_URL}/subir_foto_perfil.php`, payload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });

      const profilePath = response?.data?.foto_perfil || '';
      if (!profilePath) {
        throw new Error('No se recibio la ruta de la foto de perfil.');
      }

      const version = Date.now();
      setUploadedPath(profilePath);
      setPreviewBuster(version);
      if (typeof onUploaded === 'function') {
        onUploaded(profilePath, response?.data?.message || 'Foto de perfil actualizada correctamente', version);
      }
    } catch (uploadError) {
      const apiMessage = uploadError?.response?.data?.message;
      setError(apiMessage || 'No se pudo subir la foto de perfil.');
    } finally {
      setIsUploading(false);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async () => {
    setError('');
    setIsUploading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/eliminar_foto_perfil.php`, {}, {
        withCredentials: true
      });

      const version = Date.now();
      setUploadedPath('');
      setPreviewBuster(version);
      setUrlValue('');

      if (typeof onUploaded === 'function') {
        onUploaded('', response?.data?.message || 'Foto de perfil eliminada correctamente', version);
      }
    } catch (deleteError) {
      const apiMessage = deleteError?.response?.data?.message;
      setError(apiMessage || 'No se pudo eliminar la foto de perfil.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSetPhotoByUrl = () => {
    const normalizedUrl = String(urlValue || '').trim();
    const validationError = validateUrl(normalizedUrl);

    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    const version = Date.now();
    setUploadedPath(normalizedUrl);
    setPreviewBuster(version);

    if (typeof onUploaded === 'function') {
      onUploaded(normalizedUrl, 'URL de foto de perfil aplicada correctamente', version);
    }
  };

  return (
    <div className="rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-4">
      <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3 inline-flex items-center gap-2">
        <img src={FLATICON_ICON_PROFILE} alt="Icono perfil" className={getIconClass('w-4 h-4')} loading="lazy" />
        <span>Foto de perfil</span>
      </h4>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        className="hidden"
        disabled={disabled || isUploading}
        onChange={(event) => {
          const selectedFile = event.target.files?.[0];
          if (selectedFile) {
            handleUpload(selectedFile);
          }
        }}
      />

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50/70 dark:bg-gray-900/30">
          <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2">Desde tu dispositivo</p>
          <button
            type="button"
            disabled={disabled || isUploading}
            onClick={() => inputRef.current?.click()}
            className="w-full px-3 py-2 rounded-md border border-dashed border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:border-blue-500 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            <img src={FLATICON_ICON_UPLOAD} alt="Icono subir" className={getIconClass('w-4 h-4')} loading="lazy" />
            <span>{isUploading ? 'Subiendo foto...' : 'Seleccionar foto de perfil'}</span>
          </button>
        </div>

        <div className="min-w-0 rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50/70 dark:bg-gray-900/30">
          <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2">Pegar URL de imagen</p>
          <div className="flex flex-wrap items-stretch gap-2 lg:flex-nowrap">
            <input
              type="url"
              value={urlValue}
              onChange={(event) => setUrlValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleSetPhotoByUrl();
                }
              }}
              placeholder="https://..."
              disabled={disabled || isUploading}
              className="min-w-0 w-full flex-1 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              disabled={disabled || isUploading}
              onClick={handleSetPhotoByUrl}
              className="shrink-0 whitespace-nowrap px-3 py-2 rounded-md border border-blue-500 text-sm text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-60 inline-flex items-center gap-2"
            >
              <img src={FLATICON_ICON_URL} alt="Icono URL" className={getIconClass('w-4 h-4')} loading="lazy" />
            </button>
          </div>

          {normalizedUrlInput && (
            <div className="mt-3">
              <p className="text-[11px] text-gray-500 dark:text-gray-300 mb-2">Vista previa antes de aplicar</p>
              {urlPreviewSrc && !urlPreviewFailed ? (
                <img
                  src={urlPreviewSrc}
                  alt="Vista previa de URL"
                  className="w-20 h-20 rounded-lg object-cover border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800"
                  onError={() => setUrlPreviewFailed(true)}
                />
              ) : (
                <p className="text-xs text-amber-600 dark:text-amber-300">No se puede cargar una vista previa con esta URL.</p>
              )}
            </div>
          )}
        </div>
      </div>

      <button
        type="button"
        disabled={disabled || isUploading}
        onClick={handleDeletePhoto}
        className="w-full mt-2 px-3 py-2 rounded-md border border-red-500 text-sm text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
      >
        <img src={FLATICON_ICON_DELETE} alt="Icono borrar" className={getIconClass('w-4 h-4')} loading="lazy" />
        <span>Borrar foto de perfil</span>
      </button>

      <p className="text-[11px] text-gray-500 dark:text-gray-300 mt-2">Formatos: JPG, PNG, GIF, WEBP. Maximo: 5 MB.</p>

      {error && <p className="mt-2 text-xs text-red-600 dark:text-red-300">{error}</p>}

      {previewSrc && (
        <div className="mt-4 flex justify-center">
          <img
            src={previewSrc}
            alt="Vista previa de foto de perfil"
            className="w-32 h-32 rounded-full object-cover border-4 border-gray-200 dark:border-gray-600 shadow-md"
          />
        </div>
      )}
    </div>
  );
};

export default ProfileImageUploadField;